import React, { useState, useEffect, useCallback } from 'react';
import { adminManagementAPI, subRoleAPI, endpointAPI } from '../../../../services';
import { adminHasPermission, getCurrentAdmin, isAdminAuth } from '../../../../services/auth/authManager';
import { clearCachedSubRolePermissions } from '../../../../services/auth/permissionMiddleware';
import { useNotification } from '../../../../services/notificationService';
import { handlePermissionError } from '../../../../services/utils/permissionErrorHandler';
import {
  getConfigEndpoint, setConfigEndpoint, storeEndpointsList, getFirstActiveEndpoint
} from '../../../../services/endpoints/configEndpointManager';
import './SubRoleManagement.css';

const PERMISSION_CATEGORIES = {
  users:    { icon: 'fa-users',         label: 'Users',    perms: ['canView','canViewSpecificGroups','canViewBlocked','canManage','canBlockUnblock','canDelete'] },
  groups:   { icon: 'fa-layer-group',   label: 'Groups',   perms: ['canView','canViewSpecific','canCreate','canEdit','canDelete','canManageUsers','canManageInstruments'] },
  instruments: { icon: 'fa-chart-bar',  label: 'Instruments', perms: ['canView','canViewSpecific','canViewByCategory','canEdit'] },
  funds:    { icon: 'fa-wallet',        label: 'Funds',    perms: ['canView','canViewSpecific','canManage'] },
  positions:{ icon: 'fa-chart-line',    label: 'Positions',perms: ['canView','canViewSpecific','canViewByGroup'] },
  engines:  { icon: 'fa-server',        label: 'Engines',  perms: ['canView','canViewHistory','canManageRoles','canSelectPrimary'] },
  quoteEndpoints:      { icon: 'fa-link',       label: 'Quote Endpoints',      perms: ['canView','canEdit'] },
  tradeEngineEndpoints:{ icon: 'fa-rocket',     label: 'Trade Eng. Endpoints', perms: ['canView','canEdit'] },
  watchlists:{ icon: 'fa-star',         label: 'Watchlists',perms: ['canView','canViewSpecific','canEdit'] },
  reports:   { icon: 'fa-file-alt',     label: 'Reports',   perms: ['canView','canViewUserReports','canViewGroupReports','canViewPositionReports','canViewTradeReports'] },
  logs:      { icon: 'fa-clipboard-list',label: 'Logs',      perms: ['canView','canViewAccessLogs','canViewErrorLogs'] },
  settings:  { icon: 'fa-cog',          label: 'Settings',  perms: ['canView','canEdit'] },
  admins:    { icon: 'fa-user-shield',  label: 'Admins',    perms: ['canView','canCreate','canUpdate','canDelete','canAssignRole'] },
};

const formatPermLabel = (key) => key.replace('can', '').replace(/([A-Z])/g, ' $1').trim();

const SubRoleManagement = () => {
  const { success, error: notifyError } = useNotification();
  const [subRoles, setSubRoles] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [adminAssignments, setAdminAssignments] = useState({});
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState(null);

  // Selected sub-role & mode
  const [selectedId, setSelectedId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  // Filters
  const [mainRoleFilter, setMainRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Form for create/edit
  const [form, setForm] = useState({ name: '', displayName: '', description: '', mainRole: 'admin', priority: 1, permissions: {} });
  const [expandedCats, setExpandedCats] = useState({});
  const [showAssignPanel, setShowAssignPanel] = useState(false);

  useEffect(() => {
    if (!isAdminAuth()) { setPageError('Please log in'); return; }
    initializeEndpoint().then(() => { loadSubRoles(); loadAdminsWithAssignments(); });
  }, []);

  const initializeEndpoint = useCallback(async () => {
    if (getConfigEndpoint()) return;
    const stored = getFirstActiveEndpoint();
    if (stored) { setConfigEndpoint(stored); return; }
    try {
      const r = await endpointAPI.getAll();
      const data = typeof r.data === 'string' ? JSON.parse(r.data) : r.data;
      if (Array.isArray(data) && data.length > 0) {
        const formatted = data.map(e => ({ id: e._id, name: e.name, baseUrl: e.baseUrl || e.url, status: e.isActive ? 'active' : 'inactive' }));
        storeEndpointsList(formatted);
        const first = formatted.find(e => e.status === 'active') || formatted[0];
        if (first) setConfigEndpoint(first.baseUrl);
      }
    } catch {}
  }, []);

  const loadSubRoles = useCallback(async (retry = 0) => {
    if (!adminHasPermission('system.super')) { setPageError('Only super admins can manage sub-roles'); return; }
    setLoading(true);
    try {
      const params = {};
      if (mainRoleFilter !== 'all') params.mainRole = mainRoleFilter;
      if (statusFilter !== 'all') params.isActive = statusFilter === 'active';
      const r = await subRoleAPI.getAllSubRoles(params);
      setSubRoles(r.data || []);
      setPageError(null);
    } catch (err) {
      if ((err.response?.status === 404 || err.code === 'ERR_NETWORK') && retry < 2) {
        await initializeEndpoint();
        await new Promise(r => setTimeout(r, 500));
        return loadSubRoles(retry + 1);
      }
      setPageError(err.response?.data?.message || 'Failed to load sub-roles');
    } finally { setLoading(false); }
  }, [mainRoleFilter, statusFilter, initializeEndpoint]);

  const loadAdminsWithAssignments = useCallback(async () => {
    try {
      const r = await adminManagementAPI.getAllAdmins();
      const adminList = r.admins || [];
      setAdmins(adminList);
      const assignments = {};
      for (const a of adminList) {
        try {
          const sr = await subRoleAPI.getAdminSubRoles(a.id || a._id);
          assignments[a.id || a._id] = sr.data || [];
        } catch { assignments[a.id || a._id] = []; }
      }
      setAdminAssignments(assignments);
    } catch {}
  }, []);

  // -- Sub-role actions --
  const selectSubRole = (sr) => {
    setSelectedId(sr._id);
    setEditMode(false);
    setForm({ ...sr, permissions: sr.permissions || {} });
    setShowCreate(false);
    setShowAssignPanel(false);
    setExpandedCats({});
  };

  const startCreate = () => {
    setForm({ name: '', displayName: '', description: '', mainRole: 'admin', priority: 1, permissions: {} });
    setSelectedId(null);
    setEditMode(true);
    setShowCreate(true);
    setShowAssignPanel(false);
    setExpandedCats({});
  };

  const startEdit = () => { setEditMode(true); setShowCreate(false); };

  const cancelEdit = () => {
    if (showCreate) {
      setShowCreate(false);
      setSelectedId(null);
      setEditMode(false);
    } else {
      const original = subRoles.find(sr => sr._id === selectedId);
      if (original) { setForm({ ...original, permissions: original.permissions || {} }); }
      setEditMode(false);
    }
  };

  const togglePerm = (cat, perm) => {
    setForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [cat]: { ...prev.permissions[cat], [perm]: !prev.permissions[cat]?.[perm] }
      }
    }));
  };

  const toggleCategory = (cat) => {
    setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const handleSave = async () => {
    if (!form.name || !form.displayName) { notifyError('Name and display name are required'); return; }
    setLoading(true);
    try {
      const payload = { name: form.name, displayName: form.displayName, description: form.description, mainRole: form.mainRole, priority: form.priority, permissions: form.permissions };
      if (showCreate) {
        await subRoleAPI.createSubRole(payload);
        success('Sub-role created');
      } else {
        await subRoleAPI.updateSubRole(selectedId, payload);
        success('Sub-role updated');
      }
      clearCachedSubRolePermissions();
      setEditMode(false); setShowCreate(false);
      loadSubRoles();
    } catch (err) { handlePermissionError(err); notifyError(err.response?.data?.message || 'Failed to save sub-role'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (sr) => {
    if (!window.confirm(`Delete sub-role "${sr.displayName}"? This is a soft delete.`)) return;
    setLoading(true);
    try {
      await subRoleAPI.deleteSubRole(sr._id);
      success('Sub-role deleted');
      clearCachedSubRolePermissions();
      if (selectedId === sr._id) { setSelectedId(null); setEditMode(false); }
      loadSubRoles(); loadAdminsWithAssignments();
    } catch (err) { notifyError(err.response?.data?.message || 'Failed to delete sub-role'); }
    finally { setLoading(false); }
  };

  const handleClone = (sr) => {
    setForm({
      name: sr.name + '_copy', displayName: sr.displayName + ' (Copy)',
      description: sr.description, mainRole: sr.mainRole, priority: sr.priority,
      permissions: JSON.parse(JSON.stringify(sr.permissions || {}))
    });
    setSelectedId(null);
    setEditMode(true);
    setShowCreate(true);
    setExpandedCats({});
  };

  const handleInitDefaults = async () => {
    if (!window.confirm('Create default sub-roles for all main roles?')) return;
    setLoading(true);
    try {
      const r = await subRoleAPI.initializeDefaultSubRoles();
      success(`Created: ${r.data?.created?.length || 0}, Skipped: ${r.data?.skipped?.length || 0}`);
      loadSubRoles();
    } catch (err) { notifyError(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  // -- Assignment actions --
  const handleAdminAssign = async (adminId, subRoleId, isAssigned) => {
    setLoading(true);
    try {
      const current = adminAssignments[adminId] || [];
      const newIds = isAssigned
        ? current.filter(sr => (sr._id || sr) !== subRoleId).map(sr => sr._id || sr)
        : [...current.map(sr => sr._id || sr), subRoleId];
      await subRoleAPI.assignSubRolesToAdmin(adminId, newIds);
      clearCachedSubRolePermissions(adminId);
      success(isAssigned ? 'Sub-role removed' : 'Sub-role assigned');
      loadAdminsWithAssignments();
    } catch (err) { notifyError(err.response?.data?.message || 'Failed to update assignment'); }
    finally { setLoading(false); }
  };

  const selectedSubRole = subRoles.find(sr => sr._id === selectedId);
  const filteredSubRoles = subRoles.filter(sr => {
    const matchRole = mainRoleFilter === 'all' || sr.mainRole === mainRoleFilter;
    const matchStatus = statusFilter === 'all' || (statusFilter === 'active' ? sr.isActive : !sr.isActive);
    const matchSearch = !searchTerm || sr.name.toLowerCase().includes(searchTerm.toLowerCase()) || sr.displayName.toLowerCase().includes(searchTerm.toLowerCase());
    return matchRole && matchStatus && matchSearch;
  });

  const roleLabel = { viewer: 'Viewer', moderator: 'Moderator', admin: 'Admin' };

  if (pageError && !subRoles.length) {
    return (
      <div className="srm-container">
        <div className="srm-empty"><i className="fas fa-exclamation-triangle srm-empty-icon"></i><h3>Access Denied</h3><p>{pageError}</p>
          <button className="am-btn am-btn-primary" onClick={() => window.location.reload()}><i className="fas fa-sync"></i> Retry</button></div>
      </div>
    );
  }

  return (
    <div className="srm-container">
      {/* Header */}
      <div className="am-header">
        <div className="am-header-left">
          <div className="am-header-icon"><i className="fas fa-key"></i></div>
          <div><h1>Sub-Role Management</h1><p>{subRoles.length} sub-roles across {['viewer','moderator','admin'].map(r => subRoles.filter(sr => sr.mainRole === r).length).join('/')} roles</p></div>
        </div>
        <div className="am-header-actions">
          <button className="am-btn am-btn-ghost" onClick={loadSubRoles}><i className="fas fa-sync"></i> Refresh</button>
          <button className="am-btn am-btn-ghost" onClick={handleInitDefaults}><i className="fas fa-bolt"></i> Defaults</button>
          {adminHasPermission('system.super') && (
            <button className="am-btn am-btn-primary" onClick={startCreate}><i className="fas fa-plus"></i> Create</button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="srm-filters">
        <div className="am-search">
          <i className="fas fa-search"></i>
          <input placeholder="Search sub-roles..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <select value={mainRoleFilter} onChange={e => setMainRoleFilter(e.target.value)}>
          <option value="all">All Roles</option>
          <option value="viewer">Viewer</option>
          <option value="moderator">Moderator</option>
          <option value="admin">Admin</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Layout: List + Detail */}
      <div className="srm-layout">
        {/* Left: Sub-role list */}
        <div className="srm-list-panel">
          {loading && !subRoles.length ? (
            <div className="am-loading"><div className="am-spinner"></div><p>Loading...</p></div>
          ) : filteredSubRoles.length === 0 ? (
            <div className="srm-empty"><i className="fas fa-shield-alt srm-empty-icon"></i><h3>No sub-roles</h3><p>Create a sub-role or initialize defaults.</p></div>
          ) : (
            <div className="srm-list">
              {filteredSubRoles.map(sr => {
                const permCount = Object.entries(sr.permissions || {}).reduce((c, [, perms]) => c + Object.values(perms || {}).filter(Boolean).length, 0);
                return (
                  <div key={sr._id}
                    className={`srm-card ${selectedId === sr._id ? 'selected' : ''} ${!sr.isActive ? 'inactive' : ''}`}
                    onClick={() => selectSubRole(sr)}
                  >
                    <div className="srm-card-top">
                      <span className="srm-card-name">{sr.displayName}</span>
                      <span className={`srm-role-dot ${sr.mainRole}`}></span>
                    </div>
                    <div className="srm-card-meta">
                      <span className="srm-card-id">{sr.name}</span>
                      <span className="srm-card-priority">P{sr.priority}</span>
                      <span className="srm-card-permcount">{permCount} perms</span>
                    </div>
                    <div className="srm-card-actions" onClick={e => e.stopPropagation()}>
                      <button className="am-icon-btn" title="Clone" onClick={() => handleClone(sr)}><i className="fas fa-clone"></i></button>
                      {adminHasPermission('system.super') && (
                        <button className="am-icon-btn am-icon-danger" title="Delete" onClick={() => handleDelete(sr)}><i className="fas fa-trash"></i></button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: Detail / Edit / Create */}
        <div className="srm-detail-panel">
          {showCreate || (selectedSubRole && editMode) ? (
            /* === Edit / Create Mode === */
            <div className="srm-edit-view">
              <div className="srm-edit-header">
                <button className="am-panel-back" onClick={cancelEdit}><i className="fas fa-arrow-left"></i></button>
                <h3>{showCreate ? 'Create Sub-Role' : `Edit: ${form.displayName}`}</h3>
              </div>
              <div className="srm-edit-body">
                <div className="am-form-row">
                  <div className="am-form-group">
                    <label>Name *</label>
                    <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      required pattern="[a-z_]+" placeholder="e.g. viewer_limited"
                      disabled={!showCreate} />
                  </div>
                  <div className="am-form-group">
                    <label>Display Name *</label>
                    <input type="text" value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} required placeholder="e.g. Limited Viewer" />
                  </div>
                </div>
                <div className="am-form-group">
                  <label>Description</label>
                  <input type="text" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe this sub-role" />
                </div>
                <div className="am-form-row">
                  <div className="am-form-group">
                    <label>Main Role</label>
                    <select value={form.mainRole} onChange={e => setForm(f => ({ ...f, mainRole: e.target.value }))} disabled={!showCreate}>
                      <option value="viewer">Viewer</option>
                      <option value="moderator">Moderator</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="am-form-group">
                    <label>Priority</label>
                    <input type="number" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: parseInt(e.target.value) || 1 }))} min="1" max="10" />
                  </div>
                </div>

                {/* Permission Matrix */}
                <div className="srm-section-title">Permissions</div>
                <div className="srm-permission-matrix">
                  {Object.entries(PERMISSION_CATEGORIES).map(([cat, cfg]) => {
                    const catPerms = form.permissions[cat] || {};
                    const enabledCount = cfg.perms.filter(p => catPerms[p]).length;
                    const isExpanded = expandedCats[cat] !== false;
                    return (
                      <div key={cat} className="srm-perm-category">
                        <div className="srm-perm-cat-header" onClick={() => toggleCategory(cat)}>
                          <div className="srm-perm-cat-left">
                            <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} srm-chevron`}></i>
                            <i className={`fas ${cfg.icon} srm-cat-icon`}></i>
                            <span className="srm-cat-name">{cfg.label}</span>
                          </div>
                          <div className="srm-perm-cat-right">
                            <span className="srm-cat-count">{enabledCount}/{cfg.perms.length}</span>
                            <button className="srm-cat-toggle-all"
                              onClick={e => {
                                e.stopPropagation();
                                const allOn = enabledCount === cfg.perms.length;
                                const newPerms = {};
                                cfg.perms.forEach(p => { newPerms[p] = !allOn; });
                                setForm(prev => ({ ...prev, permissions: { ...prev.permissions, [cat]: { ...prev.permissions[cat], ...newPerms } } }));
                              }}
                            >
                              {enabledCount === cfg.perms.length ? 'Clear all' : 'Select all'}
                            </button>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="srm-perm-grid">
                            {cfg.perms.map(perm => (
                              <label key={perm} className={`srm-perm-item ${catPerms[perm] ? 'on' : 'off'}`}>
                                <input type="checkbox" checked={!!catPerms[perm]} onChange={() => togglePerm(cat, perm)} />
                                <span className="srm-perm-label">{formatPermLabel(perm)}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="srm-edit-actions">
                  <button className="am-btn am-btn-ghost" onClick={cancelEdit}>Cancel</button>
                  <button className="am-btn am-btn-primary" onClick={handleSave} disabled={loading}>
                    {loading ? 'Saving...' : showCreate ? 'Create Sub-Role' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          ) : selectedSubRole ? (
            /* === Detail View === */
            <div className="srm-detail-view">
              <div className="srm-detail-header">
                <div className="srm-detail-icon"><i className="fas fa-shield-alt"></i></div>
                <div className="srm-detail-info">
                  <h3>{selectedSubRole.displayName}</h3>
                  <span className="srm-detail-name">{selectedSubRole.name}</span>
                </div>
                <div className="srm-detail-badges">
                  <span className={`srm-role-badge ${selectedSubRole.mainRole}`}>{roleLabel[selectedSubRole.mainRole]}</span>
                  <span className={`srm-status-badge ${selectedSubRole.isActive ? 'active' : 'inactive'}`}>{selectedSubRole.isActive ? 'Active' : 'Inactive'}</span>
                  <span className="srm-priority-badge">Priority {selectedSubRole.priority}</span>
                </div>
              </div>

              <div className="srm-detail-desc">{selectedSubRole.description || 'No description'}</div>

              {/* Actions */}
              <div className="srm-detail-actions">
                {adminHasPermission('system.super') && (
                  <>
                    <button className="am-btn am-btn-primary" onClick={startEdit}><i className="fas fa-edit"></i> Edit</button>
                    <button className="am-btn am-btn-ghost" onClick={() => handleClone(selectedSubRole)}><i className="fas fa-clone"></i> Clone</button>
                    <button className="am-btn am-btn-danger" onClick={() => handleDelete(selectedSubRole)}><i className="fas fa-trash"></i> Delete</button>
                  </>
                )}
                <button className={`am-btn am-btn-ghost ${showAssignPanel ? 'active' : ''}`}
                  onClick={() => setShowAssignPanel(!showAssignPanel)}>
                  <i className="fas fa-users"></i> Admin Assignments
                </button>
              </div>

              {/* Permission Summary */}
              <div className="srm-section-title">Permissions</div>
              <div className="srm-perm-summary">
                {Object.entries(PERMISSION_CATEGORIES).map(([cat, cfg]) => {
                  const catPerms = selectedSubRole.permissions?.[cat] || {};
                  const enabled = cfg.perms.filter(p => catPerms[p]);
                  if (enabled.length === 0) return null;
                  return (
                    <div key={cat} className="srm-perm-summary-cat">
                      <i className={`fas ${cfg.icon}`}></i>
                      <span className="srm-perm-summary-label">{cfg.label}</span>
                      <div className="srm-perm-summary-items">
                        {enabled.map(p => <span key={p} className="srm-perm-tag">{formatPermLabel(p)}</span>)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Admin Assignment Panel */}
              {showAssignPanel && (
                <div className="srm-assign-section">
                  <div className="srm-section-title">Admin Assignments ({admins.filter(a => (adminAssignments[a.id || a._id] || []).some(sr => (sr._id || sr) === selectedSubRole._id)).length})</div>
                  <p className="am-muted">Toggle to assign/remove this sub-role from admins. Only admins with matching main role are shown.</p>
                  <div className="srm-assign-grid">
                    {admins.filter(a => a.role === selectedSubRole.mainRole || a.role === 'super_admin').map(a => {
                      const aid = a.id || a._id;
                      const assigned = (adminAssignments[aid] || []).some(sr => (sr._id || sr) === selectedSubRole._id);
                      return (
                        <label key={aid} className={`srm-assign-card ${assigned ? 'assigned' : ''}`}>
                          <input type="checkbox" checked={assigned}
                            onChange={() => handleAdminAssign(aid, selectedSubRole._id, assigned)} />
                          <div>
                            <strong>{a.profile?.firstName} {a.profile?.lastName}</strong>
                            <small>@{a.username}</small>
                          </div>
                          <span className={`srm-role-dot ${a.role}`} title={roleLabel[a.role] || a.role}></span>
                        </label>
                      );
                    })}
                    {admins.filter(a => a.role === selectedSubRole.mainRole || a.role === 'super_admin').length === 0 && (
                      <p className="am-muted">No admins with matching role.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Empty State */
            <div className="srm-empty-state">
              <div className="srm-empty-state-icon"><i className="fas fa-key"></i></div>
              <h3>Sub-Role Management</h3>
              <p>Select a sub-role from the list to view its details and permissions, or create a new one.</p>
              <div className="srm-empty-actions">
                <button className="am-btn am-btn-primary" onClick={startCreate}><i className="fas fa-plus"></i> Create Sub-Role</button>
                <button className="am-btn am-btn-ghost" onClick={handleInitDefaults}><i className="fas fa-bolt"></i> Initialize Defaults</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubRoleManagement;
